import React, { useState, useCallback, useImperativeHandle, forwardRef, useEffect, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { CheckCircleIcon, XCircleIcon, ClipboardIcon, ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import { useProject } from '../../../config/ProjectContext';
import { useSnackbar } from '../../../providers/SnackbarContext';
import GraphqlService from '../../../service/graphqlService';
import { calculateEndDateTimeIso, calculateStartDateTimeIso } from '../../../utils/dateUtils';
import detailsConfig from './DetailsConfig.json';
import useDeviceQuarantineStatus from '../../../hooks/useDeviceQuarantineStatus';
import DeviceDefenderActions from '../../../components/DeviceDefenderActions';
import { isDefenderEnabledProject } from '../../../config/deviceDefender';
import KotoDeviceImage from '../../../assets/images/devices/koto.png';
import MomentoDeviceImage from '../../../assets/images/devices/momento.png';
import MomentoSingleDeviceImage from '../../../assets/images/devices/momento-single.jpg';
import MomentoDoubleDeviceImage from '../../../assets/images/devices/momento-double.png';
import MomentoMilkDeviceImage from '../../../assets/images/devices/momento-milk.jpg';
import PurinaDeviceImageUrl from '../../../assets/images/devices/purina.svg?url';

const DeviceInfoTab = forwardRef(({ machineInfo, rawMachineInfo, isLoading, connectivityStatus, deviceSerial }, ref) => {
  const { project } = useProject();
  const openSnackBar = useSnackbar();

  // Device Defender quarantine status
  const { data: quarantineStatus, isLoading: isLoadingQuarantine } = useDeviceQuarantineStatus(
    project?.code,
    deviceSerial
  );

  // Helper function to determine Defender Status badge props
  const getDefenderStatusDisplay = useCallback(() => {
    // DEBUG: Log quarantine status data
    console.log('[DeviceInfoTab] getDefenderStatusDisplay called:', {
      projectCode: project?.code,
      deviceSerial,
      isLoadingQuarantine,
      quarantineStatus,
      typename: quarantineStatus?.__typename,
    });

    if (isLoadingQuarantine) {
      return { status: 'loading', text: 'Loading...', color: 'gray' };
    }

    if (!quarantineStatus || quarantineStatus.__typename === 'NotFound') {
      console.log('[DeviceInfoTab] Returning not-tracked because:', { quarantineStatus });
      return { status: 'not-tracked', text: 'Not Tracked', color: 'gray' };
    }

    if (quarantineStatus.__typename !== 'DeviceQuarantineStatusSuccess') {
      console.log('[DeviceInfoTab] Returning unknown because __typename is:', quarantineStatus.__typename);
      return { status: 'unknown', text: 'Unknown', color: 'gray' };
    }

    const { blocked, insertedOn } = quarantineStatus.data;

    if (blocked) {
      const blockedDate = insertedOn
        ? moment(insertedOn).format('YYYY-MM-DD HH:mm')
        : '';
      return {
        status: 'blocked',
        text: `Blocked${blockedDate ? ` (${blockedDate})` : ''}`,
        color: 'red'
      };
    }

    return { status: 'active', text: 'Active', color: 'green' };
  }, [quarantineStatus, isLoadingQuarantine]);

  // Notable Events state
  const [notableEvents, setNotableEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventsLimit, setEventsLimit] = useState(10); // Default to 10 events

  // Get configuration based on project - memoized to prevent infinite renders
  const getConfig = useCallback(() => {
    // Validación defensiva para evitar errores si el JSON está mal o vacío
    if (!detailsConfig || typeof detailsConfig !== 'object' || !detailsConfig.defaults || !detailsConfig.projects) {
      // Devuelve una configuración mínima para evitar crash
      return {
        Details: {},
        DevicePresence: {},
        PairedDevice: { label: 'Paired Device' },
        Modules: { display: false },
        NotableEvents: { display: false }
      };
    }

    const projectCode = project?.code?.toString();
    const defaultConfig = detailsConfig.defaults;
    // Si no hay config para el proyecto, usar un objeto vacío para que solo se apliquen los defaults
    let projectConfig = (projectCode && detailsConfig.projects[projectCode]) ? detailsConfig.projects[projectCode] : {};

    // Si el nombre del proyecto incluye 'momento', asegurar que modules esté habilitado
    const projectName = project?.name?.toLowerCase() || '';
    if (projectName.includes('momento')) {
      projectConfig = {
        ...projectConfig,
        Modules: {
          ...(projectConfig.Modules || {}),
          display: true // Forzar mostrar modules para Momento
        }
      };
    }

    // Merge default config with project-specific overrides
    const config = {};
    Object.keys(defaultConfig).forEach(section => {
      config[section] = {
        ...defaultConfig[section],
        ...(projectConfig[section] || {})
      };
    });

    return config;
  }, [project?.code, project?.name]);

  const config = useMemo(() => getConfig(), [getConfig]);

  // Get device image based on project
  const getDeviceImage = () => {
    const projectCode = project?.code;
    const projectName = project?.name?.toLowerCase() || '';
    
    switch (projectCode) {
      case 39: // KOTO project
      case '39':
        return (
          <img 
            src={KotoDeviceImage} 
            alt="KOTO Coffee Machine" 
            className="w-full h-full object-contain bg-gray-100 rounded-2xl shadow-md"
            style={{ maxWidth: '200px', maxHeight: '180px' }}
          />
        );
      case 5: // Momento project
      case '5': {
        // Determine which Momento image to use based on model name
        const modelName = machineInfo?.model?.toLowerCase() || '';
        let momentoImage = MomentoDeviceImage; // Default
        let altText = "Momento Coffee Machine";
        
        if (modelName.includes('double')) {
          momentoImage = MomentoDoubleDeviceImage;
          altText = "Momento Double Coffee Machine";
        } else if (modelName.includes('milk')) {
          momentoImage = MomentoMilkDeviceImage;
          altText = "Momento Milk Coffee Machine";
        } else if (modelName.includes('single')) {
          momentoImage = MomentoSingleDeviceImage;
          altText = "Momento Single Coffee Machine";
        }
        
        return (
          <img 
            src={momentoImage} 
            alt={altText} 
            className="w-full h-full object-contain bg-gray-100 rounded-2xl shadow-md"
            style={{ maxWidth: '200px', maxHeight: '180px' }}
          />
        );
      }
      default:
        // Check if project name contains PURINA
        if (projectName.includes('purina')) {
          return (
            <img 
              src={PurinaDeviceImageUrl} 
              alt="Purina Device" 
              className="w-full h-full object-contain"
              style={{ maxWidth: '200px', maxHeight: '180px' }}
            />
          );
        }
        // Default SVG for other projects
        return (
          <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="bg-gray-100 rounded-2xl shadow-md max-w-full md:h-full">
            {/* WiFi arcs */}
            <path d="M100 32c24 0 46.5 8.5 63.5 22.5" stroke="#222" strokeWidth="4" strokeLinecap="round" fill="none" />
            <path d="M100 50c17 0 33 6.2 45.5 16.1" stroke="#222" strokeWidth="3" strokeLinecap="round" fill="none" />
            <path d="M100 68c10.5 0 20.5 3.5 28 9.1" stroke="#222" strokeWidth="2" strokeLinecap="round" fill="none" />
            {/* Machine body */}
            <rect x="36" y="80" width="128" height="85" rx="14" fill="#333" />
            {/* Display */}
            <rect x="58" y="95" width="40" height="22" rx="4" fill="#eaf6fa" fillOpacity="0.95" />
            {/* Gear (stylized) */}
            <circle cx="156" cy="105" r="18" fill="#fff" fillOpacity="0.92" />
            <g stroke="#333" strokeWidth="2">
              <path d="M156 87v8" />
              <path d="M156 123v8" />
              <path d="M140 98l5 5" />
              <path d="M172 117l5 5" />
              <path d="M140 117l5-5" />
              <path d="M172 98l5-5" />
            </g>
            {/* Cup */}
            <rect x="104" y="155" width="32" height="18" rx="6" fill="#fff" />
            <rect x="136" y="160" width="12" height="6" rx="3" fill="#fff" />
            {/* Cup handle */}
            <ellipse cx="148" cy="163" rx="6" ry="6" stroke="#333" strokeWidth="2" fill="none" />
            {/* Tray */}
            <rect x="98" y="172" width="48" height="6" rx="3" fill="#bbb" />
            {/* Button */}
            <circle cx="58" cy="140" r="5" fill="#fff" />
            {/* Coffee drop */}
            <ellipse cx="120" cy="172" rx="3" ry="6" fill="#a67c52" />
            {/* Cup shadow */}
            <ellipse cx="120" cy="177" rx="16" ry="4" fill="#000" fillOpacity="0.10" />
          </svg>
        );
    }
  };

  // Fetch Notable Events
  const fetchNotableEvents = useCallback(async () => {
    if (!deviceSerial || !config.NotableEvents.display || !config.NotableEvents.filterByEventType?.length) {
      return;
    }

    setIsLoadingEvents(true);
    try {
      // Set up date range for the last 30 days
      const endDate = moment();
      const startDate = moment().subtract(30, 'days');

      // Build filters for device serial and event types
      // Use OR logic for event types and AND with device serial
      const deviceSerialFilter = {
        column: 'device_serial',
        operator: 'EQ',
        value: deviceSerial
      };

      let filters;
      if (config.NotableEvents.filterByEventType.length === 1) {
        // Single event type
        const eventTypeFilter = {
          column: 'event_code',
          operator: 'EQ',
          value: config.NotableEvents.filterByEventType[0].toString()
        };
        filters = JSON.stringify({
          filter: [deviceSerialFilter, eventTypeFilter]
        });
      } else {
        // Multiple event types - use OR logic
        const eventTypeFilter = {
          column: 'event_code',
          operator: 'IN',
          value: config.NotableEvents.filterByEventType.map(type => type.toString())
        };
        filters = JSON.stringify({
          filter: [deviceSerialFilter, eventTypeFilter]
        });
      }

      const params = { add_dict: true };

      const response = await GraphqlService.getTelemetryObject(
        project.code,
        'events',
        JSON.stringify(params),
        filters,
        calculateStartDateTimeIso(startDate, '00:00'),
        calculateEndDateTimeIso(endDate, '23:59'),
        eventsLimit, // use dynamic limit based on user selection
        0, // offset
        31, // max days range
        false // use device timestamp
      );

      if (response?.data) {
        const eventsData = JSON.parse(response.data);
        // Sort events by date descending (most recent first)
        const sortedEvents = eventsData.sort((a, b) => {
          const dateA = new Date(a.created_on_local || a.created_on_utc || 0);
          const dateB = new Date(b.created_on_local || b.created_on_utc || 0);
          return dateB - dateA;
        });
        setNotableEvents(sortedEvents);
      } else {
        setNotableEvents([]);
      }
    } catch (error) {
      console.error('Error fetching notable events:', error);
      openSnackBar('Failed to fetch notable events', 'error');
      setNotableEvents([]);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [deviceSerial, config.NotableEvents.display, config.NotableEvents.filterByEventType, project.code, openSnackBar, eventsLimit]);

  // Exponer la función fetchNotableEvents para que pueda ser llamada desde el componente padre
  useImperativeHandle(ref, () => ({
    fetchNotableEvents
  }));

  // Handle events limit change
  const handleEventsLimitChange = useCallback((newLimit) => {
    setEventsLimit(newLimit);
  }, []);

  // Refetch events when limit changes (only if we already have events loaded)
  useEffect(() => {
    if (notableEvents.length > 0 && deviceSerial && config.NotableEvents.display) {
      fetchNotableEvents();
    }
  }, [eventsLimit, fetchNotableEvents, notableEvents.length, deviceSerial, config.NotableEvents.display]);

  // Trigger fetchNotableEvents on mount and when deviceSerial or config.NotableEvents.display changes
  useEffect(() => {
    if (deviceSerial && config.NotableEvents.display) {
      fetchNotableEvents();
    }
  }, [fetchNotableEvents, deviceSerial, config.NotableEvents.display]);

  // Transform events data for display
  const transformEventForDisplay = (event) => {
    const eventDescription = event.dict_event?.extra?.description || '';
    const errorType = event['extra.error_type'] || '';
    const eventCode = event.dict_event?.code || event.event_code || '';
    const eventName = event.dict_event?.name || '';

    let eventDetails = '';
    if (eventDescription && errorType) {
      eventDetails = `${eventDescription}; ${errorType}`;
    } else if (eventDescription) {
      eventDetails = eventDescription;
    } else if (errorType) {
      eventDetails = errorType;
    }

    let description;
    if (eventName) {
      description = `${eventName}${eventDetails ? `: ${eventDetails}` : ''}`;
    } else {
      description = `${eventCode}${eventDetails ? `: ${eventDetails}` : ''}`;
    }

    return {
      date: event.created_on_local || event.created_on_utc || 'N/A',
      description: description.trim()
    };
  };

  if (isLoading) {
    return (
      <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
        <div className="h-2 bg-blue-500 animate-pulse w-1/2" />
      </div>
    );
  }

  if (!machineInfo) {
    return <div className="text-gray-700">No Device Info available.</div>;
  }

  // Handle copying JSON to clipboard
  const handleCopyJson = async () => {
    try {
      // Use rawMachineInfo if available (includes null values), otherwise fall back to machineInfo
      const dataToExport = rawMachineInfo || machineInfo;
      const jsonString = JSON.stringify(dataToExport, null, 2);
      await navigator.clipboard.writeText(jsonString);
      openSnackBar('JSON copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy JSON: ', err);
      openSnackBar('Failed to copy JSON to clipboard', 'error');
    }
  };

  /**
   * IAM identifier prefixes that indicate BLE connection (AWS IAM credentials)
   * @see https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_identifiers.html#identifiers-prefixes
   */
  const IAM_PREFIXES = [
    'AIDA', // IAM user
    'AROA', // IAM role
    'ASIA', // STS temporary credentials
    'ABIA', // AWS STS service bearer token
    'ACCA', // Context-specific credential
    'AGPA', // IAM group
    'AIPA', // EC2 instance profile
    'AKIA', // IAM access key
    'ANPA', // IAM managed policy
    'ANVA', // IAM managed policy version
    'APKA', // IAM public key
    'ASCA', // IAM certificate
  ];

  /**
   * Determine connection type based on principalID
   * @see https://docs.aws.amazon.com/iot/latest/developerguide/life-cycle-events.html
   * 
   * WIFI connections use certificate IDs: 64-character hex strings
   * BLE connections use IAM credentials: Start with IAM prefixes (AIDA, AROA, ASIA, etc.)
   */
  const getConnectionType = () => {
    if (!connectivityStatus?.connected) return null;

    const principalId = connectivityStatus?.principalId || '';
    if (!principalId) return null;
    
    // Check for IAM credential prefixes (BLE connection)
    const upperPrincipalId = principalId.toUpperCase();
    const hasIamPrefix = IAM_PREFIXES.some((prefix) => upperPrincipalId.startsWith(prefix));
    if (hasIamPrefix) {
      return 'BLE';
    }
    
    // Certificate IDs are 64-character hex strings (WIFI connection)
    const hexPattern = /^[a-f0-9]{64}$/i;
    if (hexPattern.test(principalId)) {
      return 'WIFI';
    }
    
    return null;
  };

  // Get color class for connection type indicator
  const getConnectionTypeColor = (type) => {
    switch (type) {
      case 'WIFI':
        return 'bg-blue-500';
      case 'BLE':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Get firmware version label based on project
  const getFirmwareLabel = () => {
    const detailsConfig = config.Details;
    return detailsConfig.fw || 'Main Firmware Version';
  };

  // Get hardware version label based on project
  const getHardwareLabel = () => {
    const detailsConfig = config.Details;
    return detailsConfig.hw || 'Main Hardware Version';
  };

  // Helper function to handle unavailable values and objects
  const getDisplayValue = (value, defaultText = 'unavailable') => {
    if (value === null || value === undefined || value === '') {
      return defaultText;
    }
    if (typeof value === 'object') {
      try {
        // Render as a pretty JSON string, but compact for inline display, and wrap long lines
        return (
          <span
            style={{
              fontFamily: 'monospace',
              color: '#444',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere',
              maxWidth: 220,
              display: 'inline-block',
              verticalAlign: 'top',
            }}
          >
            {JSON.stringify(value)}
          </span>
        );
      } catch (e) {
        return defaultText;
      }
    }
    // For long strings, wrap and break
    if (typeof value === 'string' && value.length > 30) {
      return (
        <span
          style={{
            wordBreak: 'break-all',
            overflowWrap: 'anywhere',
            maxWidth: 220,
            display: 'inline-block',
            verticalAlign: 'top',
          }}
        >
          {value}
        </span>
      );
    }
    return value;
  };

  // Render bullet list for extra details
  const renderExtraDetails = (extra) => {
    if (!extra || typeof extra !== 'object') {
      return (
        <span className="text-gray-600">unavailable</span>
      );
    }

    const hasValidEntries = Object.entries(extra).some(([, value]) =>
      value !== null && value !== undefined && value !== ''
    );

    if (!hasValidEntries) {
      return (
        <span className="text-gray-600">unavailable</span>
      );
    }

    // Get translation mappings from config if available
    const projectCode = project?.code?.toString();
    const extraTranslations = (projectCode && detailsConfig.projects[projectCode]?.ExtraTranslations) 
      ? detailsConfig.projects[projectCode].ExtraTranslations 
      : {};

    // Sin scroll, dejar crecer el panel
    return (
      <div className="mt-1" style={{ wordBreak: 'break-all' }}>
        <ul className="pl-4 m-0 list-disc">
          {Object.entries(extra).map(([key, value]) => {
            // Use translated label if available, otherwise use original key
            const displayLabel = extraTranslations[key] || key;
            return (
              <li key={key} className="my-1 text-sm" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                <strong>{displayLabel}:</strong> {getDisplayValue(value)}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Detect if paired device is mobile (iPhone, iPad, or Android)
  const isMobileDevice = (deviceModel) => {
    if (!deviceModel) return false;
    const modelLower = deviceModel.toString().toLowerCase();
    // Check for Apple devices
    const isApple = modelLower.includes('iphone') || modelLower.includes('ipad');
    // Check for common Android device models (brands and model patterns)
    const isAndroid = modelLower.includes('android') || 
                     modelLower.includes('pixel') || 
                     modelLower.includes('galaxy') || 
                     modelLower.includes('xiaomi') ||
                     modelLower.includes('oneplus') || 
                     modelLower.includes('huawei') ||
                     modelLower.includes('samsung') ||
                     modelLower.includes('motorola') ||
                     modelLower.includes('nokia') ||
                     modelLower.includes('realme') ||
                     modelLower.includes('oppo') ||
                     modelLower.includes('vivo') ||
                     modelLower.includes('poco') ||
                     modelLower.includes('redmi') ||
                     // Samsung model patterns (SC-XXXX, SM-XXXX)
                     /^(SC|SM)-/.test(deviceModel) ||
                     // Sony Xperia patterns (XXXSO)
                     /SO$/.test(deviceModel) ||
                     // Huawei patterns (ADA-ALXX, HMA-LXX, etc.)
                     /^[A-Z]{2,3}-[A-Z]{2}\d{2}/.test(deviceModel);
    
    const isMobile = isApple || isAndroid;
    console.log('isMobileDevice check - model:', deviceModel, 'result:', isMobile);
    return isMobile;
  };

  // Map Android API levels to OS versions
  const getAndroidVersionFromApiLevel = (apiLevel) => {
    const apiLevelMap = {
      '36': 'Android 16 (API 36)',
      '35': 'Android 15 (API 35)',
      '34': 'Android 14 (API 34)',
      '33': 'Android 13 (API 33)',
      '32': 'Android 12 (API 32)',
      '31': 'Android 12 (API 31)',
      '30': 'Android 11 (API 30)',
      '29': 'Android 10 (API 29)',
      '28': 'Android 9 (API 28)',
      '27': 'Android 8.1 (API 27)',
      '26': 'Android 8 (API 26)',
      '25': 'Android 7.1 (API 25)',
      '24': 'Android 7 (API 24)',
      '23': 'Android 6 (API 23)',
      '22': 'Android 5.1 (API 22)',
      '21': 'Android 5 (API 21)',
    };
    return apiLevelMap[apiLevel] || apiLevel;
  };

  // Detect if device is Android and translate version
  const isAndroidDevice = (deviceModel) => {
    if (!deviceModel) return false;
    const modelLower = deviceModel.toString().toLowerCase();
    // It's Android if it's a mobile device but NOT iPhone/iPad
    const isApple = modelLower.includes('iphone') || modelLower.includes('ipad');
    // Detect common Android device models
    const isCommonAndroid = modelLower.includes('android') || 
                           modelLower.includes('pixel') || 
                           modelLower.includes('galaxy') || 
                           modelLower.includes('xiaomi') ||
                           modelLower.includes('oneplus') || 
                           modelLower.includes('huawei') ||
                           modelLower.includes('samsung') ||
                           modelLower.includes('motorola') ||
                           modelLower.includes('nokia') ||
                           modelLower.includes('realme') ||
                           modelLower.includes('oppo') ||
                           modelLower.includes('vivo') ||
                           modelLower.includes('poco') ||
                           modelLower.includes('redmi') ||
                           // Samsung model patterns (SC-XXXX, SM-XXXX)
                           /^(SC|SM)-/.test(deviceModel) ||
                           // Sony Xperia patterns (XXXSO)
                           /SO$/.test(deviceModel) ||
                           // Huawei patterns (ADA-ALXX, HMA-LXX, etc.)
                           /^[A-Z]{2,3}-[A-Z]{2}\d{2}/.test(deviceModel);
    return !isApple && (isCommonAndroid || /^\d+\./.test(deviceModel));
  };

  // Get display version - translate Android API levels to readable names
  const getDisplayVersion = (version, deviceModel) => {
    if (!deviceModel || !version) return version;
    
    // If it's Android and version looks like an API level, translate it
    if (isAndroidDevice(deviceModel)) {
      const numericVersion = version.match(/^\d+/);
      if (numericVersion) {
        return getAndroidVersionFromApiLevel(numericVersion[0]);
      }
    }
    return version;
  };

  // Render paired device information based on configuration and available data
  const renderPairedDeviceInfo = () => {
    const pairedDeviceConfig = config.PairedDevice;
    let deviceGateway = machineInfo.device_gateway || rawMachineInfo?.device_gateway;

    // If no device gateway data available, show placeholder
    if (!deviceGateway) {
      return (
        <span className="text-sm text-gray-600">unavailable</span>
      );
    }

    // Parse device_gateway if it's a JSON string
    if (typeof deviceGateway === 'string') {
      try {
        deviceGateway = JSON.parse(deviceGateway);
      } catch (e) {
        console.error('Failed to parse device_gateway JSON:', e);
        return (
          <span className="text-sm text-gray-600">unavailable</span>
        );
      }
    }

    // Determine if this is a mobile device (iPhone/iPad only)
    const isMobile = isMobileDevice(deviceGateway.model);
    // Use "OS Version" label for iPhone/iPad, otherwise use config label
    const versionLabel = isMobile ? 'OS Version' : pairedDeviceConfig.version;
    
    // Get the raw version value
    const rawVersion = deviceGateway.version || deviceGateway.os_version || deviceGateway.fw;
    // Translate version if needed (for Android devices)
    const displayVersion = getDisplayVersion(rawVersion, deviceGateway.model);

    return (
      <>
        {/* Model field - can come from device_gateway.model or be generic */}
        {pairedDeviceConfig.model && deviceGateway.model !== undefined && (
          <div className="mb-1 text-sm">
            <span className="font-semibold text-gray-700">Model: </span>
            <span className="text-gray-700">
              {getDisplayValue(deviceGateway.model || deviceGateway.device_model)}
            </span>
          </div>
        )}

        {/* Version field - can come from device_gateway.version or os_version */}
        {pairedDeviceConfig.version && deviceGateway.version !== undefined && (
          <div className="mb-1 text-sm">
            <span className="font-semibold text-gray-700">{versionLabel}: </span>
            <span className="text-gray-700">
              {getDisplayValue(displayVersion)}
            </span>
          </div>
        )}

        {/* App version field - comes from device_gateway.app */}
        {pairedDeviceConfig.app && deviceGateway.app !== undefined && (
          <div className="mb-1 text-sm">
            <span className="font-semibold text-gray-700">App Version: </span>
            <span className="text-gray-700">
              {getDisplayValue(deviceGateway.app || deviceGateway.app_version)}
            </span>
          </div>
        )}

        {/* SDK version field - comes from device_gateway.sdk */}
        {deviceGateway.sdk !== undefined && (
          <div className="mb-1 text-sm">
            <span className="font-semibold text-gray-700">SDK Version: </span>
            <span className="text-gray-700">
              {getDisplayValue(deviceGateway.sdk)}
            </span>
          </div>
        )}

        {/* MSL version field - comes from device_gateway.msl */}
        {deviceGateway.msl !== undefined && (
          <div className="mb-1 text-sm">
            <span className="font-semibold text-gray-700">MSL Version: </span>
            <span className="text-gray-700">
              {getDisplayValue(deviceGateway.msl)}
            </span>
          </div>
        )}

        {/* Render any extra details if available - only show if there are valid entries */}
        {deviceGateway.extra && typeof deviceGateway.extra === 'object' && 
         Object.entries(deviceGateway.extra).some(([, value]) => value !== null && value !== undefined && value !== '') && (
          <div className="mt-2 text-sm">
            <span className="font-semibold text-gray-700">Additional Details: </span>
            {renderExtraDetails(deviceGateway.extra)}
          </div>
        )}
      </>
    );
  };

  const connectionType = getConnectionType();

  return (
    <div className="p-4">
      {/* Responsive flex layout for main info and side sections */}
      <div className="flex flex-col md:flex-row gap-6 w-full">
        {/* Left Side - Device Image and Details */}
        <div className="w-full md:w-1/2 flex flex-col">
          <div className="flex flex-col md:flex-row gap-4 h-full">
            {/* Device Image */}
            <div className="flex-shrink-0 flex justify-center items-start md:items-stretch md:w-1/3 w-full">
              {getDeviceImage()}
            </div>
            {/* Device Details */}
            <div className="relative flex-1 border border-gray-300 rounded-md bg-white p-4 min-w-0 w-full max-w-full flex flex-col">
              {/* Copy JSON Icon Button - top right, subtle */}
              <button
                onClick={handleCopyJson}
                title="Copy JSON"
                className="absolute top-2 right-2 p-1 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-400 hover:text-blue-600 transition"
                style={{ fontSize: 0, lineHeight: 0 }}
              >
                <ClipboardIcon className="w-4 h-4" />
              </button>
              <div className="text-sm">
                <span className="font-semibold text-gray-700">Model: </span>
                <span className="text-gray-700">{getDisplayValue(machineInfo.model)}</span>
              </div>
              <div className="text-sm">
                <span className="font-semibold text-gray-700">Market: </span>
                <span className="text-gray-700">{getDisplayValue(machineInfo.country_code)}</span>
              </div>
              {config.Details.fw !== null && (
                <div className="text-sm">
                  <span className="font-semibold text-gray-700">{getFirmwareLabel()}: </span>
                  <span className="text-gray-700">{getDisplayValue(machineInfo.fw)}</span>
                </div>
              )}
              {config.Details.hw !== null && (
                <div className="text-sm">
                  <span className="font-semibold text-gray-700">{getHardwareLabel()}: </span>
                  <span className="text-gray-700">{getDisplayValue(machineInfo.hw)}</span>
                </div>
              )}
              {/* Additional Details */}
              <div className="mt-2 text-sm">
                <span className="font-semibold text-gray-700">Additional Details: </span>
                {renderExtraDetails(machineInfo.extra)}
              </div>
            </div>
          </div>
        </div>
        {/* Right Side - Device Presence and Paired Device */}
        <div className="flex flex-col md:w-1/2 w-full gap-4">
          {/* Device Presence Section */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Device Presence</h3>
            <div className="bg-white border border-gray-300 rounded-md p-4">
              <div className="flex items-center mb-2">
                {connectivityStatus?.connected ? (
                  <>
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm text-gray-900">Connected</span>
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-sm text-gray-900">Disconnected</span>
                  </>
                )}
              </div>
              <div className="mb-1 text-sm">
                <span className="font-semibold text-gray-700">Last Updated: </span>
                <span className="text-gray-700">
                  {connectivityStatus?.lastUpdate
                    ? moment(connectivityStatus.lastUpdate).format('YYYY-MM-DD HH:mm:ss')
                    : 'unavailable'} (UTC)
                </span>
              </div>
              {connectivityStatus?.disconnectReason && (
                <div className="mb-1 text-sm">
                  <span className="font-semibold text-gray-700">Reason: </span>
                  <span className="text-gray-700">{connectivityStatus.disconnectReason}</span>
                </div>
              )}
              {connectionType && (
                <div className="mb-1 text-sm flex items-center">
                  <span className="font-semibold text-gray-700">Connection Type: </span>
                  <span className={`w-2.5 h-2.5 rounded-full ml-2 mr-1.5 ${getConnectionTypeColor(connectionType)}`} />
                  <span className="text-gray-700">{getDisplayValue(connectionType)}</span>
                </div>
              )}
              {connectivityStatus?.ssid && config.DevicePresence.SSID !== null && (
                <div className="mb-1 text-sm">
                  <span className="font-semibold text-gray-700">SSID: </span>
                  <span className="text-gray-700">{getDisplayValue(connectivityStatus.ssid)}</span>
                </div>
              )}
              {/* Device Defender Status - Visible only for Device-Defender-enabled projects (see config/deviceDefender) */}
              {/* Hide section while loading, if device is not tracked, or API error */}
              {(() => {
                // DEBUG: Log the condition check
                const projectCodeNum = Number(project?.code);
                const isDefenderProject = isDefenderEnabledProject(project?.code);
                console.log('[DeviceInfoTab] Defender section check:', {
                  projectCode: project?.code,
                  projectCodeNum,
                  isDefenderProject,
                });
                return isDefenderProject;
              })() && (() => {
                const defenderStatus = getDefenderStatusDisplay();
                console.log('[DeviceInfoTab] Defender render decision:', {
                  defenderStatus,
                  willRender: defenderStatus.status !== 'loading' && defenderStatus.status !== 'not-tracked' && defenderStatus.status !== 'unknown',
                });
                // Don't show section if loading, not tracked, or unknown (API error)
                if (defenderStatus.status === 'loading' || defenderStatus.status === 'not-tracked' || defenderStatus.status === 'unknown') {
                  return null;
                }
                return (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center flex-wrap">
                      <>
                        {defenderStatus.color === 'green' && (
                          <ShieldCheckIcon className="w-5 h-5 text-green-600 mr-2" />
                        )}
                        {defenderStatus.color === 'red' && (
                          <ShieldExclamationIcon className="w-5 h-5 text-red-600 mr-2" />
                        )}
                        {defenderStatus.color === 'gray' && (
                          <ShieldCheckIcon className="w-5 h-5 text-gray-400 mr-2" />
                        )}
                        <span className="text-sm font-semibold text-gray-700 mr-2">Defender Status:</span>
                        <span className={`text-sm ${
                          defenderStatus.color === 'green' ? 'text-green-600' :
                          defenderStatus.color === 'red' ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          {defenderStatus.text}
                        </span>
                        {/* Device Defender Actions - Inline Block/Unblock buttons */}
                        <DeviceDefenderActions
                          projectId={project?.code}
                          deviceSerial={deviceSerial}
                          isBlocked={quarantineStatus?.data?.blocked || false}
                          defenderStatus={defenderStatus.status}
                        />
                      </>
                </div>
              </div>
                );
              })()}
            </div>
          </div>
          {/* Paired Device Section - Only show if not explicitly hidden */}
          {config.PairedDevice.display !== false && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                {config.PairedDevice.label}
              </h3>
              <div className="bg-white border border-gray-300 rounded-md p-4">
                {renderPairedDeviceInfo()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add a visual line to split sections */}
      <div className="w-full">
        <hr className="border-t border-gray-300 my-4" />
      </div>

      {/* Modules Section: Only visible for Momento project */}
      {config.Modules.display && (
        <div className="w-full">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Modules</h3>
          {machineInfo.modules && machineInfo.modules.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {machineInfo.modules.map((module, index) => (
                <div key={index} className="bg-white border border-gray-300 rounded-md p-4">
                  <h4 className="text-sm mb-2">
                    <span className="font-semibold text-gray-700">Serial: </span>
                    <span className="text-gray-700">{getDisplayValue(module.serial, `Module ${index + 1}`)}</span>
                  </h4>
                  <div className="mb-1 text-sm">
                    <span className="font-semibold text-gray-700">Firmware Version: </span>
                    <span className="text-gray-700">{getDisplayValue(module.fw)}</span>
                  </div>
                  <div className="mb-1 text-sm">
                    <span className="font-semibold text-gray-700">Hardware Version: </span>
                    <span className="text-gray-700">{getDisplayValue(module.hw)}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="font-semibold text-gray-700">Additional Details: </span>
                    {renderExtraDetails(module.extra)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-gray-300 rounded-md p-4 text-gray-500 text-sm">
              No modules available for this device.
            </div>
          )}
        </div>
      )}

      {/* Notable Events Section */}
      {config.NotableEvents.display && (
        <>
          {/* Línea separadora: solo si no hay módulos, para evitar doble línea */}
          {config.Modules.display && (
            <div className="w-full">
              <hr className="border-t border-gray-300 my-4" />
            </div>
          )}
          <div className="w-full">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-semibold text-gray-900">Notable Events</h3>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={eventsLimit}
                  onChange={(e) => handleEventsLimitChange(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>Last 10 events</option>
                  <option value={25}>Last 25 events</option>
                  <option value={50}>Last 50 events</option>
                </select>
              </div>
            </div>
            {isLoadingEvents ? (
              <div className="text-center py-4">
                <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
                  <div className="h-2 bg-blue-500 animate-pulse w-1/2" />
                </div>
                <p className="text-sm text-gray-600 mt-2">Loading events...</p>
              </div>
            ) : notableEvents.length > 0 ? (
              <div>
                <table className="w-full border border-gray-300 shadow-sm">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-semibold text-gray-700 text-sm">Event Date</th>
                      <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-semibold text-gray-700 text-sm">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notableEvents.map((event, index) => {
                      const transformedEvent = transformEventForDisplay(event);
                      const eventDate = event.created_on_local || event.created_on_utc;
                      const formattedDate = eventDate
                        ? moment(eventDate).format('DD/MM/YYYY HH:mm:ss')
                        : 'N/A';

                      return (
                        <tr key={index}>
                          <td className="border border-gray-300 px-4 py-2 text-gray-700 text-sm">
                            {formattedDate}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-gray-700 text-sm">
                            {transformedEvent.description}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-2 text-right">
                  <p className="text-xs text-gray-500">
                    Showing latest {notableEvents.length} notable events from the last 30 days
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-600">No notable events found in the last 30 days.</p>
              </div>
            )}
          </div>
        </>
      )}

    </div>
  );
});

DeviceInfoTab.propTypes = {
  machineInfo: PropTypes.shape({
    model: PropTypes.string,
    country_code: PropTypes.string,
    fw: PropTypes.string,
    hw: PropTypes.string,
    extra: PropTypes.object,
    device_gateway: PropTypes.object, // Paired device information
    modules: PropTypes.arrayOf(PropTypes.shape({
      serial: PropTypes.string,
      fw: PropTypes.string,
      hw: PropTypes.string,
      extra: PropTypes.object,
    })),
  }),
  rawMachineInfo: PropTypes.object, // Raw data with null values preserved
  isLoading: PropTypes.bool,
  connectivityStatus: PropTypes.shape({
    connected: PropTypes.bool,
    lastUpdate: PropTypes.string,
    principalId: PropTypes.string,
    ssid: PropTypes.string,
  }),
  deviceSerial: PropTypes.string, // Device serial for fetching events
};

DeviceInfoTab.defaultProps = {
  machineInfo: null,
  rawMachineInfo: null,
  isLoading: false,
  connectivityStatus: null,
  deviceSerial: null,
};

export default DeviceInfoTab;
